import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Dark mode is a first-class theme, not a filter over the light one.
 *
 * The stored value is the *preference* — including "match my device" — and the
 * applied class is derived from it, so a user who follows their device gets the
 * change when their phone switches at sunset without touching Trinity.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'trinity.theme';

interface ThemeState {
  preference: ThemePreference;
  /** What is actually on screen right now. */
  resolved: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
}

const systemPrefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolve = (preference: ThemePreference): 'light' | 'dark' =>
  preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference;

const apply = (resolved: 'light' | 'dark'): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
};

export const useTheme = create<ThemeState>()(
  persist(
    (set) => ({
      preference: 'system',
      resolved: resolve('system'),
      setPreference: (preference) => {
        const resolved = resolve(preference);
        apply(resolved);
        set({ preference, resolved });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ preference: state.preference }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const resolved = resolve(state.preference);
        apply(resolved);
        state.resolved = resolved;
      },
    },
  ),
);

/**
 * Called once at startup. Keeps a "match my device" user in step with their device
 * for as long as the tab is open.
 */
export const watchSystemTheme = (): (() => void) => {
  if (typeof window === 'undefined') return () => {};

  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    const { preference } = useTheme.getState();
    if (preference !== 'system') return;
    const resolved = systemPrefersDark() ? 'dark' : 'light';
    apply(resolved);
    useTheme.setState({ resolved });
  };

  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
};
